import { Component } from '@theme/component';

/**
 * A custom element that counts down to a recurring daily reset time.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} hours - The hours digit element.
 * @property {HTMLElement} minutes - The minutes digit element.
 * @property {HTMLElement} seconds - The seconds digit element.
 *
 * @extends Component<Refs>
 */
class CountdownBarComponent extends Component {
  requiredRefs = ['hours', 'minutes', 'seconds'];

  connectedCallback() {
    super.connectedCallback();

    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#interval) clearInterval(this.#interval);
  }

  /** @type {number | null} */
  #interval = null;

  /**
   * Next occurrence of the configured reset hour, in local time.
   * @returns {Date}
   */
  #getTarget() {
    const resetHour = Number(this.getAttribute('data-reset-hour') ?? 0);
    const now = new Date();
    const target = new Date(now.getFullYear(), now.getMonth(), now.getDate(), resetHour, 0, 0, 0);

    if (target <= now) {
      target.setDate(target.getDate() + 1);
    }

    return target;
  }

  #tick() {
    const remainingMs = this.#getTarget().getTime() - Date.now();
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const { hours: hoursEl, minutes: minutesEl, seconds: secondsEl } = this.refs;

    hoursEl.textContent = String(hours).padStart(2, '0');
    minutesEl.textContent = String(minutes).padStart(2, '0');
    secondsEl.textContent = String(seconds).padStart(2, '0');
  }
}

if (!customElements.get('countdown-timer-bar')) {
  customElements.define('countdown-timer-bar', CountdownBarComponent);
}
